import type {
  ColDef,
  ColGroupDef,
  ValueFormatterParams
} from 'ag-grid-community'
import { captureException } from '@sentry/react'

import { IExtendedColDef } from 'app/services/api/agGrid/agGrid'
import { generateColDefsIgnoreFields } from 'common/utils/agGrid/helpers'
import { chopAndFormatNumberWithCommas } from 'common/utils/chart/chartUtils'

type ValueFormatterOverrideMap = Record<
  string,
  NonNullable<ColDef['valueFormatter']>
>
type Overrides = ValueFormatterOverrideMap | ValueFormatterOverrideMap[]

/*
  TODO: [FE DEBT] Generate a Global colDefs/colGroupDefs Intersection Type
   We treat colDefs as a union, but it is closer to an intersection type.
   Investigate if we can get away with simply merging both objects, or
   if we are better off picking some fields and generating our own type.
*/
type AllDef = ColDef & ColGroupDef

// this is slightly different from generateColDefsIgnoreFields
// since year fields can be null, we can't ignore formatting them
export const overrideValueFormatter = (
  colDef: ColDef,
  overrides: Overrides
) => {
  // TODO: [FE DEBT] Remove This Type Assertion After Generating colDefs Intersection Type
  const allDef = colDef as AllDef

  // technically, ColGroupDef should not have a field property
  if (typeof allDef.field === 'string') {
    const key = allDef.field

    const _overrideMap = Array.isArray(overrides)
      ? overrides.find((overrideMap) => overrideMap[key])
      : overrides

    const override = _overrideMap?.[key]
    const isGroupDef = Array.isArray(allDef.children)

    if (override) {
      // if colDef is a colGroupDef, the formatter is applied to the children
      if (isGroupDef) {
        return {
          ...colDef,
          children: allDef.children.map((colDefChild) => ({
            ...colDefChild,
            valueFormatter: override
          }))
        }
      }

      // doing separate return because scientificNotationValueFormatter seems to check
      // children by hasOwnProperty and triggers forEach of undefined error
      return {
        ...colDef,
        valueFormatter: override
      }
    }

    return allDef
  }

  return allDef
}

export const generateOverrideMap = (
  colDefs: ColDef[],
  overrideField: string,
  overrideFormatter: NonNullable<ColDef['valueFormatter']>,
  colDefKey: keyof IExtendedColDef = 'field'
): ValueFormatterOverrideMap =>
  generateColDefsIgnoreFields(colDefs, overrideField, colDefKey)?.reduce(
    (overrideMap: ValueFormatterOverrideMap, key: string) => {
      overrideMap[key] = overrideFormatter

      return overrideMap
    },
    {}
  ) || {}

export const processFalsyValue = <T>(value: T): string | T => {
  if (value === null || value === undefined || value === '') {
    return 'NULL'
  }

  return value
}

export const formatYearColDefs = <TData, TValue>(
  params: ValueFormatterParams<TData, TValue>
) => params.value ?? 'NULL'

export const floatValueFormatter = <TData, TValue>(
  params: ValueFormatterParams<TData, TValue>
) => {
  if (typeof params.value === 'number') {
    return chopAndFormatNumberWithCommas(params.value, {
      maximumFractionDigits: 3,
      minimumFractionDigits: 3
    })
  }

  return params.value ?? 'NULL'
}

export const applyFloatValueFormatter = (colDef: ColDef) => {
  if ('children' in colDef && Array.isArray(colDef.children)) {
    colDef.children.forEach(applyFloatValueFormatter)
  }

  colDef.valueFormatter = floatValueFormatter
}

export const standardDateFormatter = <TData, TValue>(
  params: ValueFormatterParams<TData, TValue>
) => {
  if (params.value === null || params.value === undefined) {
    return 'NULL'
  }

  const dateStr = String(params.value)

  try {
    // Try to parse as ISO date first (handles both full ISO and just YYYY-MM-DD)
    if (dateStr.includes('T')) {
      // Extract just the date portion (YYYY-MM-DD) from ISO string
      const isoDateStr = dateStr.split('T')[0]

      return isoDateStr
    }

    // For MM/DD/YYYY format
    if (dateStr.includes('/')) {
      const [month, day, year] = dateStr.split('/')
      const formattedDateStr = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`

      return formattedDateStr
    }

    // For other date formats, try to parse as Date object
    const date = new Date(dateStr)

    if (!isNaN(date.getTime())) {
      const dateObjStr = date.toISOString().split('T')[0]

      return dateObjStr
    }

    // If all parsing fails, return the original value
    return dateStr
  } catch (e) {
    captureException(e, {
      tags: {
        function: 'standardDateFormatter'
      },
      extra: {
        inputValue: params.value,
        stringValue: dateStr,
        columnField: params.colDef?.field
      }
    })

    return dateStr
  }
}

export const lengthFormatter = <TData, TValue>(
  params: ValueFormatterParams<TData, TValue>
) => {
  if (typeof params.value === 'number') {
    return params.value !== 0
      ? chopAndFormatNumberWithCommas(params.value, {
          minimumFractionDigits: 3,
          maximumFractionDigits: 3
        })
      : '0'
  }

  return params.value ?? 'NULL'
}

export const nullValueFormatter = <TData, TValue>(
  params: ValueFormatterParams<TData, TValue>
) => params.value ?? 'NULL'

export const getValueFormatter = (
  additionalIgnoreFields: string[],
  fieldsToShowEntireValue: string[],
  field?: string
) => {
  if (field === 'IsolationSectionVolume') {
    return (params: ValueFormatterParams) =>
      typeof params.value === 'number'
        ? chopAndFormatNumberWithCommas(params.value, {
            minimumFractionDigits: 5,
            maximumFractionDigits: 5
          })
        : (params.value ?? 'NULL')
  }

  if (field === 'From Measure' || field === 'To Measure') {
    return (params: ValueFormatterParams) =>
      typeof params.value === 'number'
        ? chopAndFormatNumberWithCommas(params.value, {
            minimumFractionDigits: 3,
            maximumFractionDigits: 3
          })
        : (params.value ?? 'NULL')
  }

  if (field?.includes('Length')) {
    return lengthFormatter
  }

  if (field && fieldsToShowEntireValue.includes(field)) {
    return (params: ValueFormatterParams) =>
      typeof params.value === 'number' ? params.value : 'NULL'
  }

  return field && additionalIgnoreFields.includes(field)
    ? floatValueFormatter
    : nullValueFormatter
}
